/**
 * Modal 类型定义
 * 定义模态框相关的类型和接口
 */

/**
 * 模态框类型枚举
 * @typedef {"confirm" | "alert" | "cancel"} ModalType
 */
export type ModalType = "confirm" | "alert" | "cancel";

/**
 * 模态框状态枚举
 * @typedef {"success" | "error" | "warn" | ""} ModalStatus
 */
export type ModalStatus = "success" | "error" | "warn" | "";

/**
 * 模态框类型常量
 * 提供类型安全的常量值
 */
export const MODAL_TYPES = {
  /** 确认类型 */
  CONFIRM: "confirm" as const,
  /** 警告类型 */
  ALERT: "alert" as const,
  /** 取消类型 */
  CANCEL: "cancel" as const,
} as const;

/**
 * 模态框状态常量
 * 提供类型安全的状态值
 */
export const MODAL_STATUS = {
  /** 成功状态 */
  SUCCESS: "success" as const,
  /** 错误状态 */
  ERROR: "error" as const,
  /** 警告状态 */
  WARN: "warn" as const,
  /** 无状态 */
  NONE: "" as const,
} as const;

/**
 * 按钮配置接口
 * 控制模态框按钮的显示
 */
export interface ButtonConfig {
  /** 是否显示确认按钮 */
  showConfirm: boolean;
  /** 是否显示取消按钮 */
  showCancel: boolean;
}

/**
 * 模态框属性接口
 * 定义模态框组件的所有可配置属性
 */
export interface ModalProps {
  // 基础属性
  /** 模态框宽度，支持 CSS 单位 */
  width?: string;
  /** 模态框标题 */
  title?: string;
  /** 模态框内容文本 */
  content?: string;
  /** 模态框类型 */
  type?: ModalType;
  /** 模态框状态 */
  status?: ModalStatus;
  /** 确认按钮文本 */
  confirmText?: string;
  /** 取消按钮文本 */
  cancelText?: string;

  // 显示控制
  /** 是否显示关闭按钮 */
  showClose?: boolean;
  /** 是否显示按钮区域 */
  showBtn?: boolean;
  /** 是否显示模态框 */
  show?: boolean;
  /** 是否显示确认按钮 */
  showConfirmButton?: boolean;
  /** 是否显示取消按钮 */
  showCancelButton?: boolean;

  // 事件回调
  /** 确认按钮点击事件回调 */
  onConfirm?: (event?: Event) => void;
  /** 取消按钮点击事件回调 */
  onCancel?: (event?: Event) => void;
  /** 关闭按钮点击事件回调 */
  onClose?: (event?: Event) => void;

  // 样式和布局
  /** 自定义 CSS 类名 */
  class?: string;
  /** 自定义 CSS 类名（别名） */
  className?: string;
  /** 是否将模态框挂载到 body 元素 */
  appendToBody?: boolean;
  /** 内容是否自动调整高度 */
  contentAuto?: boolean;
  /** 确认按钮是否显示加载状态 */
  confirmLoading?: boolean;

  // 特殊功能
  /** 是否为登录模态框 */
  login?: boolean;
  /** 是否为注册模态框 */
  register?: boolean;

  // 插槽
  /** 默认插槽内容 */
  children?: any;
  /** 追加内容插槽 */
  append?: any;
}

/**
 * 模态框事件类型
 * 定义模态框可能触发的事件
 */
export interface ModalEvents {
  /** 确认事件 */
  confirm: Event;
  /** 取消事件 */
  cancel: Event;
  /** 关闭事件 */
  close: Event;
}
